import type {AxiosRequestConfig, CancelTokenSource, Method} from 'axios';
import axios from 'axios';
import configStore from '@deezer/next/services/config';
import {ApiErrorCode} from '@constants/errorCodes';
import requestStorage from './requestStorage';
import {isErrorCode} from '@deezer/next/services/api/utils';
import {Routes} from '@constants/routes';
import type {
	DataResponseWrapper,
	PaginatedGet,
	PaginationMeta,
} from './requests/types';
import type {MergeDeep} from 'type-fest';
import Router from 'next/router';
import HttpStatusCode from '@deezer/next/services/types/HttpStatusCode';
import endpoints from '@deezer/next/services/api/Starmania/endpoints';

type ApiConfig = {
	requestId?: string;
} & AxiosRequestConfig;

const call = async <T = any>(
	method: Method,
	path: string,
	config?: ApiConfig,
): Promise<T> => {
	const requestOptions: AxiosRequestConfig = {
		headers: {
			'X-Requested-With': 'XMLHttpRequest',
		},
	};

	if (config?.requestId) {
		const source: CancelTokenSource = axios.CancelToken.source();
		requestOptions.cancelToken = source.token;
		requestStorage.add(config.requestId, source);
	}

	const url = `https://${configStore.get('API_HOST')}${path}`;

	try {
		const response = await axios({
			url,
			method,
			...requestOptions,
			...config,
		});
		return response.data as T; // TODO this should be removed once Axios provide the right response types
	} catch (e: any) {
		const errorCode =
			e.response?.data?.errors?.[0]?.code ||
			e.response?.data?.errors?.[0]?.code;

		if (isErrorCode(errorCode, ApiErrorCode.PASSWORD_EXPIRED)) {
			await Router.replace(Routes.PASSWORD_FORCE_RESET_NOTICE);
		} else if (
			path !== endpoints.auth.getToken &&
			path !== endpoints.auth.revokeToken &&
			(e.response?.status === HttpStatusCode.UNAUTHORIZED ||
				isErrorCode(errorCode, [
					ApiErrorCode.TOKEN_EXPIRED,
					ApiErrorCode.TOKEN_MALFORMED,
					ApiErrorCode.DISABLED_ACCOUNT,
					ApiErrorCode.UNKNOWN_REVOKED_TOKEN,
					ApiErrorCode.TOKEN_INVALID,
				]))
		) {
			if (
				Router.pathname !== Routes.LOGOUT &&
				Router.pathname !== Routes.HOME
			) {
				await Router.replace(Routes.LOGOUT);
			}
		}

		throw e;
	} finally {
		requestStorage.remove(config?.requestId);
	}
};

const get = <T = any>(path: string, config?: ApiConfig) =>
	call<T>('get', path, config);

export const sortParams = (
	sort: string | undefined,
	sortOrder: string | undefined,
	config?: ApiConfig,
) => {
	return {
		...config,
		params: {
			...config?.params,
			sort: sort || undefined,
			sortOrder: sortOrder || undefined,
		},
	};
};

const paginatedGet = <T = any, Meta = {}>(
	path: string,
	config?: ApiConfig,
): PaginatedGet<T, MergeDeep<PaginationMeta, Meta>> => {
	return (pageNumber = 1, pageSize = 50) =>
		get<DataResponseWrapper<T, MergeDeep<PaginationMeta, Meta>>>(path, {
			...config,
			params: {
				...config?.params,
				'page[limit]': pageSize,
				'page[offset]': (pageNumber - 1) * pageSize,
			},
		});
};

export default {
	get,
	paginatedGet,
	post: <T = any>(path: string, data?: any, config?: ApiConfig) =>
		call<T>('post', path, {...config, data}),
	patch: <T = any>(path: string, data?: any, config?: ApiConfig) =>
		call<T>('patch', path, {...config, data}),
	put: <T = any>(path: string, data?: any, config?: ApiConfig) =>
		call<T>('put', path, {...config, data}),
	delete: <T = any>(path: string, data?: any, config?: ApiConfig) =>
		call<T>('delete', path, {...config, data}),
	cancel: requestStorage.cancel,
};
